/**
 * Pure depth-ordering rule for the dimetric scene. Given a renderable's tile
 * position (`x`, `y`, `z`) and a {@link Layer} class, it defines the total order
 * in which renderables are painted: back-to-front, so a thing nearer the viewer
 * draws over a thing further back.
 *
 * This module owns *order* only. {@link projection.tileToScreen} stays the single
 * source of truth for *position* (it already folds `z` into screen-Y via
 * `Z_STEP`); the depth key deliberately does not re-derive screen coordinates.
 *
 * Pure and rendering-free: it imports no Pixi and no `render/`, so the renderer
 * can map this order onto `zIndex` without the sim ever learning how it draws.
 */

/**
 * The draw-layer class a renderable belongs to. Ordered so that, on the same
 * tile and elevation, ground paints first, then statics, then mobiles - i.e. the
 * player stands on top of the static it shares a tile with. The numeric values
 * are the tie-break order and must stay ascending.
 */
export enum Layer {
  Ground = 0,
  Static = 1,
  Mobile = 2,
}

/** Anything that can be depth-sorted: a tile position plus its layer class. */
export interface Renderable {
  x: number;
  y: number;
  z: number;
  layer: Layer;
  /**
   * True when this renderable is a roof static. When a roof and a wall/floor
   * static are tied at the same tile *and* `z`, the roof draws behind (see
   * {@link compareDepth}), so the perimeter parapet course stays solid against its
   * own roof cap. A roof still draws in front of (hides) the lower-`z` interior
   * statics under it. Only meaningful for {@link Layer.Static}; defaults to falsey.
   */
  roof?: boolean;
  /**
   * True for UO static art carrying TileFlag.Foliage.
   * A canopy and its trunk occupy the same tile and elevation, so foliage must
   * win their exact depth tie instead of inheriting source-record order.
   */
  foliage?: boolean;
}

/**
 * Three-way comparator for {@link Array.prototype.sort}: returns < 0 when `a`
 * draws behind `b` (painted first), > 0 when `a` draws in front, 0 when they
 * occupy the exact same depth slot.
 *
 * Ordering, most significant first:
 *   1. greater `(x + y)` draws in front (nearer the viewer in the dimetric grid);
 *   2. then greater `z` draws in front (higher elevation), so the roof correctly
 *      hides the interior statics (partition walls, wall-hung paintings) sitting
 *      below it;
 *   3. then, between two statics tied at the same `z`, a roof tile draws behind a
 *      non-roof static (wall/floor), so the perimeter parapet course that shares
 *      the roof's exact `z` stays solid instead of letting the flat roof cap clip
 *      it (an insertion-order coin-flip otherwise);
 *   4. then the layer class (ground < static < mobile) breaks the tie, so a
 *      mobile and a static sharing a tile and z resolve the same way every time.
 *   5. finally, between otherwise tied statics, UO foliage draws over its trunk.
 */
export function compareDepth(a: Renderable, b: Renderable): number {
  const depthA = a.x + a.y;
  const depthB = b.x + b.y;
  if (depthA !== depthB) return depthA - depthB;
  if (a.z !== b.z) return a.z - b.z;
  if (a.layer === Layer.Static && b.layer === Layer.Static && !!a.roof !== !!b.roof) {
    return a.roof ? -1 : 1;
  }
  if (a.layer === Layer.Static && b.layer === Layer.Static && !!a.foliage !== !!b.foliage) {
    return a.foliage ? 1 : -1;
  }
  return a.layer - b.layer;
}

/**
 * Returns a new array of the given renderables sorted back-to-front (the order
 * in which they should be painted). Does not mutate the input.
 */
export function sortByDepth<T extends Renderable>(items: readonly T[]): T[] {
  return [...items].sort(compareDepth);
}

/**
 * Sort the given renderables back-to-front **in place**, returning the same
 * array. Use this only where the caller owns a throwaway array (e.g. the
 * renderer's per-frame `sortables` buffer): it produces the identical order to
 * {@link sortByDepth} but skips the defensive copy, saving a full-scene array
 * allocation every frame. Prefer {@link sortByDepth} when the input must be
 * preserved.
 */
export function sortByDepthInPlace<T extends Renderable>(items: T[]): T[] {
  return items.sort(compareDepth);
}
